import { Fragment, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import {
  ChevronDown,
  ChevronsUp,
  ChevronUp,
  Edit,
  Eye,
  ListCheck,
  Plus,
  Search,
  X,
} from 'lucide-react';

import HighlightedText from '@/components/HighlightedText';
import StatusBadge from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { TooltipButton } from '@/components/ui/tooltip-button';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { usePersistentState } from '@/hooks/usePersistentState';
import { useQualities } from '@/hooks/useQualitiesAPI';
import PageLayout from '@/layouts/PageLayout';
import { getUserInfo } from '@/lib/auth';
import { apiService } from '@/lib/axios';
import {
  MASTER_LIST_FILTER_GROUPS,
  PENDING_STATUSES,
  cn,
  getCategoryItems,
  getClosestActionPlanImplementAt,
  getQualitySearchFields,
  matchesFieldsSearch,
} from '@/lib/utils';

const joinCategoryItems = (categories) => categories.join(';');
// Pending feature: the Categories sub-row on the Master List. Everything
// behind it (dialog, chips, PATCH /qualities/:id/categories, e2e test) is
// built and kept intentionally — flip this to true to ship it.
const SHOW_MASTER_LIST_CATEGORIES = false;

const MasterTabs = ({ activeTab, setActiveTab, setSearchQuery }) => {
  const navigate = useNavigate();

  const handleTab = (value) => {
    navigate('.', { replace: true, state: value });
    setActiveTab(value);
    setSearchQuery('');
  };

  return (
    <Tabs value={activeTab} onValueChange={handleTab}>
      <TabsList className="w-full flex gap-x-2 gap-y-1 flex-wrap">
        <TabsTrigger value="master">Quality Event Master List</TabsTrigger>
        <TabsTrigger value="pending">Pending Quality Events</TabsTrigger>
      </TabsList>
    </Tabs>
  );
};

const PendingTabs = ({ handleFilter, activeFilter }) => {
  return (
    <Tabs onValueChange={handleFilter} value={activeFilter}>
      <TabsList
        className={cn(
          'w-full flex gap-x-2 gap-y-1 flex-wrap',
          'bg-gray-50 rounded-t-none'
        )}
      >
        <TabsTrigger value="attention">Your Attention Needed</TabsTrigger>
        <TabsTrigger value="approved">Approved Quality Events</TabsTrigger>
        <TabsTrigger value="other">Other Pending Quality Events</TabsTrigger>
      </TabsList>
    </Tabs>
  );
};

const QualitiesTableHeader = ({
  activeTab,
  setSortBy,
  setSortDirection,
  sortBy,
  sortDirection,
}) => {
  const isMasterView = activeTab === 'master';

  const getSortIcon = (column) => {
    if (sortBy !== column)
      return <ChevronDown className="h-4 w-4 opacity-50" />;

    return sortDirection === 'asc' ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  const handleSort = (column) => {
    if (sortBy === column)
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  return (
    <TableHeader>
      <TableRow>
        <TableHead>
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold hover:bg-transparent"
            onClick={() => handleSort('serial')}
          >
            QE Number
            {getSortIcon('serial')}
          </Button>
        </TableHead>
        <TableHead>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold hover:bg-transparent"
                  onClick={() => handleSort('nc')}
                >
                  NC?
                  {getSortIcon('nc')}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>NonConformity</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </TableHead>
        {!isMasterView && (
          <TableHead>
            <Button
              variant="ghost"
              className="h-auto p-0 font-semibold hover:bg-transparent"
              onClick={() => handleSort('createdAt')}
            >
              Reported At
              {getSortIcon('createdAt')}
            </Button>
          </TableHead>
        )}
        {!isMasterView && (
          <TableHead>
            <Button
              variant="ghost"
              className="h-auto p-0 font-semibold hover:bg-transparent"
              onClick={() => handleSort('creator')}
            >
              Reporter
              {getSortIcon('creator')}
            </Button>
          </TableHead>
        )}
        <TableHead>
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold hover:bg-transparent"
            onClick={() => handleSort('informationDescription')}
          >
            Description of Quality Event
            {getSortIcon('informationDescription')}
          </Button>
        </TableHead>
        <TableHead>
          <Button
            variant="ghost"
            className={cn(
              'h-auto p-0 w-42',
              'font-semibold text-left whitespace-break-spaces',
              'hover:bg-transparent'
            )}
            onClick={() => handleSort('implementAt')}
          >
            Implementation Date
            {getSortIcon('implementAt')}
          </Button>
        </TableHead>
        <TableHead className="min-w-48">
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold hover:bg-transparent"
            onClick={() => handleSort('status')}
          >
            Status
            {getSortIcon('status')}
          </Button>
        </TableHead>
        <TableHead
          className={cn(
            'sticky right-0 z-10',
            'w-[124px] font-semibold',
            'backdrop-blur bg-background/95 supports-[backdrop-filter]:bg-background/80',
            'before:absolute before:h-full before:left-0 before:top-0 before:w-px',
            'before:bg-gray-200'
          )}
        >
          Actions
        </TableHead>
      </TableRow>
    </TableHeader>
  );
};

const QualitiesTableBody = ({
  activeTab,
  activeFilter,
  canReleaseQuality,
  qualityList,
  refetch,
  searchQuery,
}) => {
  const navigate = useNavigate();
  const { showConfirmModal } = useConfirmModal();
  const isMasterView = activeTab === 'master';
  const userSub = getUserInfo()?.sub;
  const [categoryQuality, setCategoryQuality] = useState(null);
  const [categoryValue, setCategoryValue] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [isSavingCategory, setIsSavingCategory] = useState(false);
  const [deletingCategoryKey, setDeletingCategoryKey] = useState(null);
  const [hoveredQualityId, setHoveredQualityId] = useState(null);

  const closeCategoryDialog = () => {
    setCategoryQuality(null);
    setCategoryValue('');
    setCategoryError('');
  };

  const openCategoryDialog = (qualityItem) => {
    setCategoryQuality(qualityItem);
    setCategoryValue('');
    setCategoryError('');
  };

  const handleAddCategory = async (event) => {
    event.preventDefault();

    const nextCategory = categoryValue.trim();
    if (!nextCategory) {
      setCategoryError('Category is required.');
      return;
    }

    if (nextCategory.includes(';')) {
      setCategoryError('Use one category at a time.');
      return;
    }

    const categories = getCategoryItems(categoryQuality?.categories);
    const nextCategories = joinCategoryItems([...categories, nextCategory]);

    try {
      setIsSavingCategory(true);
      setCategoryError('');
      await apiService.qualities.updateCategories(
        categoryQuality.id,
        nextCategories
      );
      await refetch?.();
      closeCategoryDialog();
    } catch (error) {
      setCategoryError(
        error.response?.data?.error || 'Failed to add category.'
      );
    } finally {
      setIsSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (qualityItem, categoryIndex) => {
    const categories = getCategoryItems(qualityItem.categories);
    const nextCategories = joinCategoryItems(
      categories.filter((_, index) => index !== categoryIndex)
    );
    const deleteKey = `${qualityItem.id}-${categoryIndex}`;

    try {
      setDeletingCategoryKey(deleteKey);
      await apiService.qualities.updateCategories(
        qualityItem.id,
        nextCategories
      );
      await refetch?.();
    } finally {
      setDeletingCategoryKey(null);
    }
  };

  const confirmDeleteCategory = (qualityItem, categoryIndex, categoryName) => {
    showConfirmModal({
      title: 'Delete Category',
      description: `Are you sure you want to delete "${categoryName}" from this quality event?`,
      confirmText: 'Delete',
      variant: 'destructive',
      onConfirm: () => handleDeleteCategory(qualityItem, categoryIndex),
    });
  };

  return (
    <>
      <TableBody>
        {qualityList.map((qualityItem) => {
          const canEdit =
            ['DRAFT', 'RETURNED'].includes(qualityItem.status) &&
            qualityItem.creator === userSub;

          return (
            <Fragment key={qualityItem.id}>
              <TableRow
                className={cn(
                  isMasterView && 'border-dotted',
                  hoveredQualityId === qualityItem.id && 'bg-muted/50'
                )}
                onMouseEnter={() => setHoveredQualityId(qualityItem.id)}
                onMouseLeave={() => setHoveredQualityId(null)}
              >
                <TableCell>
                  <HighlightedText
                    text={qualityItem.serial || qualityItem.id}
                    query={searchQuery}
                  />
                </TableCell>
                <TableCell>
                  <HighlightedText
                    text={qualityItem.nc ? 'Yes' : 'No'}
                    query={searchQuery}
                  />
                </TableCell>
                {!isMasterView && (
                  <TableCell>
                    <HighlightedText
                      text={qualityItem.createdAt?.split('T')[0]}
                      query={searchQuery}
                    />
                  </TableCell>
                )}
                {!isMasterView && (
                  <TableCell>
                    <HighlightedText
                      text={qualityItem.creatorName}
                      query={searchQuery}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <HighlightedText
                    text={qualityItem.informationDescription || '-'}
                    query={searchQuery}
                  />
                </TableCell>
                <TableCell>
                  <HighlightedText
                    text={getClosestActionPlanImplementAt(qualityItem)}
                    query={searchQuery}
                  />
                </TableCell>
                <TableCell>
                  <StatusBadge
                    status={qualityItem.status}
                    query={searchQuery}
                  />
                </TableCell>
                <TableCell
                  className={cn(
                    'sticky right-0 z-10',
                    'space-x-2 whitespace-nowrap',
                    'backdrop-blur bg-background/95 supports-[backdrop-filter]:bg-background/80',
                    'after:absolute after:h-px after:left-0 after:-top-px after:w-full',
                    'before:absolute before:h-full before:left-0 before:top-0 before:w-px',
                    'after:bg-gray-200 before:bg-gray-200',
                    hoveredQualityId === qualityItem.id &&
                      'bg-muted/95 supports-[backdrop-filter]:bg-muted/80'
                  )}
                >
                  <TooltipButton
                    onClick={() => navigate(`/quality/${qualityItem.id}`)}
                    size="sm"
                    tooltip="Preview"
                    variant="outline"
                  >
                    <Eye className="h-4 w-4" />
                  </TooltipButton>
                  {isMasterView ? (
                    <TooltipButton
                      onClick={() =>
                        navigate(`/quality/${qualityItem.id}/progression`)
                      }
                      size="sm"
                      tooltip={isMasterView ? 'View QE Progress' : 'Preview'}
                      variant="outline"
                    >
                      <ListCheck className="h-4 w-4" />
                    </TooltipButton>
                  ) : activeFilter === 'approved' && canReleaseQuality ? (
                    <TooltipButton
                      onClick={() =>
                        navigate(`/quality/${qualityItem.id}/release`, {
                          state: {
                            creatorName: qualityItem.creatorName,
                            id: qualityItem.id,
                            planChecked: qualityItem.planChecked,
                          },
                        })
                      }
                      size="sm"
                      tooltip="Release"
                      variant="outline"
                    >
                      <ChevronsUp className="h-4 w-4" />
                    </TooltipButton>
                  ) : (
                    canEdit && (
                      <TooltipButton
                        onClick={() =>
                          navigate(`/quality/${qualityItem.id}/edit`)
                        }
                        size="sm"
                        tooltip="Edit QE"
                        variant="outline"
                      >
                        <Edit className="h-4 w-4" />
                      </TooltipButton>
                    )
                  )}
                </TableCell>
              </TableRow>
              {isMasterView && SHOW_MASTER_LIST_CATEGORIES && (
                <TableRow
                  className={
                    hoveredQualityId === qualityItem.id && 'bg-muted/50'
                  }
                  onMouseEnter={() => setHoveredQualityId(qualityItem.id)}
                  onMouseLeave={() => setHoveredQualityId(null)}
                >
                  <TableCell className="py-3" colSpan={6}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-muted-foreground">
                        Categories
                      </span>
                      {getCategoryItems(qualityItem.categories).map(
                        (item, index) => (
                          <div
                            className={cn(
                              'group/category relative overflow-hidden',
                              'px-2 py-1',
                              'bg-gray-200 rounded-md text-nowrap text-xs'
                            )}
                            key={`${item}-${index}`}
                          >
                            {item}
                            <button
                              aria-label={`Delete ${item}`}
                              className={cn(
                                'absolute right-1 top-1/2 -translate-y-1/2',
                                'hidden h-4 w-4 items-center justify-center rounded-full',
                                'bg-gray-300 text-gray-600 transition-colors',
                                'hover:bg-gray-400 hover:text-gray-950',
                                'focus:flex focus:outline-none',
                                'group-hover/category:flex'
                              )}
                              disabled={
                                deletingCategoryKey ===
                                `${qualityItem.id}-${index}`
                              }
                              onClick={() =>
                                confirmDeleteCategory(qualityItem, index, item)
                              }
                              type="button"
                            >
                              <X className="h-2.5 w-2.5" />
                            </button>
                          </div>
                        )
                      )}
                      <TooltipButton
                        onClick={() => openCategoryDialog(qualityItem)}
                        size="xs"
                        tooltip="Add"
                        variant="ghost"
                      >
                        <Plus />
                      </TooltipButton>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          );
        })}
      </TableBody>
      <Dialog
        open={Boolean(categoryQuality)}
        onOpenChange={(open) => !open && closeCategoryDialog()}
      >
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleAddCategory}>
            <DialogHeader>
              <DialogTitle>Add Category</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-2">
              <Input
                autoFocus
                value={categoryValue}
                onChange={(event) => setCategoryValue(event.target.value)}
                placeholder="Category"
              />
              {categoryError && (
                <p className="text-sm text-destructive">{categoryError}</p>
              )}
            </div>
            <DialogDescription />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeCategoryDialog}
              >
                Cancel
              </Button>
              <Button disabled={isSavingCategory} type="submit">
                Add
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

const QualityListPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('serial');
  const [sortDirection, setSortDirection] = useState('asc');
  const [activeTab, setActiveTab] = useState(location.state || 'master');
  const [activeFilter, handleFilter] = useState('attention');
  const [searchQuery, setSearchQuery] = useState('');
  const [includeRejected, setIncludeRejected] = usePersistentState(
    'qualities.includeRejected',
    false
  );

  const { qualities, refetch } = useQualities();

  const isMasterView = activeTab === 'master';
  const userGroups = getUserInfo()?.['cognito:groups'] || [];
  const canFilter = userGroups.some((role) =>
    MASTER_LIST_FILTER_GROUPS.includes(role)
  );
  const canReleaseQuality = userGroups.includes('QA');
  const userSub = getUserInfo()?.sub;

  const applySearch = (list, isMasterViewFlag) =>
    list.filter((item) =>
      matchesFieldsSearch(
        getQualitySearchFields(
          item,
          isMasterViewFlag,
          SHOW_MASTER_LIST_CATEGORIES
        ),
        searchQuery
      )
    );

  const applySort = (list, sortBy, sortDirection) => {
    if (!sortBy) return list;

    return [...list].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === 'implementAt') {
        aVal = getClosestActionPlanImplementAt(a);
        bVal = getClosestActionPlanImplementAt(b);
      }

      if (sortBy === 'category') {
        aVal = getCategoryItems(a.categories)[0] ?? '';
        bVal = getCategoryItems(b.categories)[0] ?? '';
      }

      // Convert boolean to string
      if (typeof aVal === 'boolean') aVal = aVal ? 'Yes' : 'No';
      if (typeof bVal === 'boolean') bVal = bVal ? 'Yes' : 'No';

      aVal = aVal ?? '';
      bVal = bVal ?? '';

      const result = String(aVal).localeCompare(String(bVal));

      return sortDirection === 'asc' ? result : -result;
    });
  };

  const baseList = qualities.filter((quality) => {
    const { status } = quality;
    const getSub = (r) => (typeof r === 'object' ? r?.sub : r);

    if (activeTab === 'master') {
      if (status === 'REJECTED') return canFilter && includeRejected;
      return ['CLOSED', 'PENDING_ACTION', 'PENDING_EC'].includes(status);
    }

    if (activeFilter === 'attention') {
      if (status === 'REVIEWING')
        return (
          getSub(quality.reviewerPipeline?.[quality.currentActorIndex]) ===
          userSub
        );

      if (status === 'RETURNED') return quality.creator === userSub;

      if (status === 'DRAFT') return quality.creator === userSub;

      return false;
    }

    if (activeFilter === 'approved') return ['APPROVED'].includes(status);

    if (activeFilter === 'other') {
      // Guards the statuses below, keeping RELEASED/CLOSED/PENDING_* out of
      // "pending" even if a future edit adds an admin bypass like CC/Documents.
      if (!PENDING_STATUSES.includes(status)) return false;

      // Drafts are private to their creator ("Your Attention Needed").
      if (status === 'DRAFT') return false;

      if (status === 'REVIEWING')
        return (
          getSub(quality.reviewerPipeline?.[quality.currentActorIndex]) !==
          userSub
        );

      if (status === 'RETURNED') return quality.creator !== userSub;

      return false;
    }
  });

  const qualityList = applySort(
    applySearch(baseList, isMasterView),
    sortBy,
    sortDirection
  );

  return (
    <PageLayout
      title={
        isMasterView ? 'Quality Event Master List' : 'Pending Quality Events'
      }
      toolbarTail={
        <Button onClick={() => navigate('/quality/new')} variant="outline">
          <Plus />
          Report a Quality Event
        </Button>
      }
    >
      <MasterTabs {...{ activeTab, setActiveTab, setSearchQuery }} />
      {!isMasterView && <PendingTabs {...{ handleFilter, activeFilter }} />}
      <div className="flex gap-4 items-center mt-4 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-10 text-lg font-semibold"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search across all fields..."
        />
        {isMasterView && canFilter && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="ml-auto shrink-0">
                Filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Include</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={includeRejected}
                onCheckedChange={setIncludeRejected}
              >
                Rejected Quality Events
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="mt-4 rounded-md border">
        <Table>
          <QualitiesTableHeader
            {...{
              activeTab,
              setSortBy,
              setSortDirection,
              sortBy,
              sortDirection,
            }}
          />
          <QualitiesTableBody
            {...{
              activeTab,
              activeFilter,
              canReleaseQuality,
              qualityList,
              refetch,
              searchQuery,
            }}
          />
        </Table>
        {!qualityList.length && (
          <div className="py-8 text-center text-gray-400">No Data</div>
        )}
      </div>
    </PageLayout>
  );
};

export default QualityListPage;
